<template>
  <div class="mobile-surrender">
    <div class="mobile-surrender__header sticky-top text-white p-3">
      <b-row align-v="center">
        <b-col cols="2">
          <b-button variant="link" @click="$router.go(-1)" class="text-white p-0">
            <AppIcon name="back" font-scale="1.5" />
          </b-button>
        </b-col>
        <b-col cols="8" class="text-center">
          <h5 class="mb-0">{{ isEditing ? 'Revisar Rendición' : 'Rendir Cobranzas' }}</h5>
          <small v-if="isEditing && surrender" class="text-white-50">#{{ surrender.id }} — {{ formatDate(surrender.date) }}</small>
          <small v-else-if="!loading && pendingPayments.length" class="text-white-50">{{ pendingPayments.length }} cobranza{{ pendingPayments.length !== 1 ? 's' : '' }} pendiente{{ pendingPayments.length !== 1 ? 's' : '' }}</small>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button variant="link" class="text-white p-0" @click="load" :disabled="loading">
            <AppIcon name="refresh" font-scale="1.2" />
          </b-button>
        </b-col>
      </b-row>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width:2.5rem;height:2.5rem" />
      <p class="mt-3 text-muted">{{ isEditing ? 'Cargando rendición...' : 'Cargando cobranzas...' }}</p>
    </div>

    <div v-else class="p-3 pb-5">
      <div v-if="isEditing" class="mb-4">
        <b-alert show variant="warning" class="mb-0 d-flex align-items-center">
          <AppIcon name="warning" class="mr-2" font-scale="1.3" />
          <div>
            <strong>Esta rendición requiere revisión.</strong><br>
            <small v-if="surrender.review_observations"><b>Motivo:</b> {{ surrender.review_observations }}</small>
            <small v-else>Corregí las cobranzas que sean necesarias y volvé a enviar.</small>
          </div>
        </b-alert>
      </div>

      <div v-else-if="surrendersToReview.length" class="mb-4">
        <div class="mobile-surrender__section-label mb-2">
          <AppIcon name="exclamation-circle" fill class="mr-1 text-danger" />Para revisar
        </div>
        <b-card v-for="item in surrendersToReview" :key="item.id" no-body class="mb-2 border-danger" style="cursor:pointer" @click="openSurrender(item)">
          <b-card-body class="py-2 px-3">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <div class="font-weight-bold" style="font-size:.9rem">Rendición #{{ item.id }} — {{ formatDate(item.date) }}</div>
                <small class="text-muted">{{ item.detail ? item.detail.length : 0 }} cobranza{{ item.detail && item.detail.length !== 1 ? 's' : '' }}<span v-if="item.review_observations"> · <b>Motivo:</b> {{ item.review_observations }}</span></small>
              </div>
              <div class="d-flex align-items-center">
                <b-badge variant="danger" class="mr-2">Revisar</b-badge>
                <AppIcon name="chevron-right" class="text-danger" />
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div v-if="!isEditing && !pendingPayments.length" class="text-center py-4">
        <EmptyState icon="check2-circle" title="Sin cobranzas pendientes" description="Todas las cobranzas ya fueron rendidas." />
        <b-button variant="outline-secondary" class="mt-3" @click="$router.go(-1)"><AppIcon name="back" class="mr-1" />Volver</b-button>
      </div>

      <template v-else-if="paymentRows.length">
        <b-card class="mb-4">
          <b-form-group label="Observaciones (opcional)" label-size="sm" class="mb-0">
            <app-textarea v-model="form.observations" rows="3" placeholder="Agregar observaciones..." />
          </b-form-group>
        </b-card>

        <div class="mobile-surrender__section-label mb-2">
          <AppIcon name="receipt" class="mr-1" />{{ isEditing ? 'Cobranzas de la rendición' : 'Cobranzas a rendir' }}
        </div>
        <b-card no-body class="mb-4">
          <div class="mobile-surrender__table-header d-flex px-3 py-2">
            <div class="flex-grow-1 small font-weight-bold text-muted">Cliente</div>
            <div class="mobile-surrender__amount small font-weight-bold text-muted text-right">Efectivo</div>
            <div class="mobile-surrender__amount small font-weight-bold text-muted text-right">Cheque</div>
            <div style="width:32px" />
          </div>
          <div v-for="(payment, index) in paymentRows" :key="payment.id" class="mobile-surrender__payment-row d-flex align-items-center px-3 py-2" :class="{ 'border-top': index > 0 }">
            <div class="flex-grow-1 pr-2">
              <div class="font-weight-bold" style="font-size:.9rem">{{ payment.customer }}</div>
              <small class="text-muted">{{ formatDate(payment.date) }}</small>
            </div>
            <div class="mobile-surrender__amount text-right">
              <span v-if="payment.efectivo > 0" class="text-success font-weight-bold" style="font-size:.9rem">$ {{ formatMoney(payment.efectivo) }}</span>
              <span v-else class="text-muted">—</span>
            </div>
            <div class="mobile-surrender__amount text-right">
              <span v-if="payment.cheque > 0" class="font-weight-bold" style="font-size:.9rem">
                $ {{ formatMoney(payment.cheque) }}
                <AppIcon name="info-circle" class="text-primary ml-1" style="cursor:pointer;font-size:.85rem" v-b-tooltip.html.left="payment.chequeTooltip" />
              </span>
              <span v-else class="text-muted">—</span>
            </div>
            <div style="width:32px" class="text-right pl-1">
              <b-button variant="link" class="p-0" @click="openEditPayment(payment)">
                <AppIcon name="edit" class="text-primary" font-scale="1.1" />
              </b-button>
            </div>
          </div>
        </b-card>

        <div class="mobile-surrender__section-label mb-2"><AppIcon name="calculator" class="mr-1" />Resumen</div>
        <b-card class="mb-4">
          <b-row class="mb-2 align-items-center">
            <b-col cols="6" class="text-muted small">Cobranzas</b-col>
            <b-col cols="6" class="text-right"><b-badge variant="secondary" pill>{{ paymentRows.length }}</b-badge></b-col>
          </b-row>
          <b-row class="mb-2 align-items-center">
            <b-col cols="6" class="text-muted small"><AppIcon name="cash-stack" class="mr-1 text-success" />Efectivo</b-col>
            <b-col cols="6" class="text-right font-weight-bold text-success">$ {{ formatMoney(totalEfectivo) }}</b-col>
          </b-row>
          <b-row class="mb-2 align-items-center">
            <b-col cols="6" class="text-muted small"><AppIcon name="credit-card" class="mr-1 text-primary" />Cheques</b-col>
            <b-col cols="6" class="text-right font-weight-bold text-primary">$ {{ formatMoney(totalCheque) }}</b-col>
          </b-row>
          <hr class="my-2">
          <b-row class="align-items-center">
            <b-col cols="5" class="font-weight-bold">Total</b-col>
            <b-col cols="7" class="text-right"><span class="h5 mb-0 font-weight-bold text-dark">$ {{ formatMoney(totalGeneral) }}</span></b-col>
          </b-row>
        </b-card>
      </template>
    </div>

    <b-modal v-model="editModal.active" centered hide-footer :key="editModal.key">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
        <h5 class="app-modal-header__title">Editar cobranza</h5>
      </div>
      <div v-if="editModal.payment">
        <div class="mb-3">
          <strong>{{ editModal.payment.customer }}</strong>
          <div class="text-muted small">{{ formatDate(editModal.payment.date) }}</div>
        </div>
        <div class="d-flex align-items-center mb-2">
          <b-form-select v-model="editModal.newItem.type" :options="typeOptions" style="flex:1;margin-right:8px" />
          <b-form-input v-model="editModal.newItem.amount" type="number" placeholder="Monto" style="flex:1;margin-right:8px" />
          <b-button variant="primary" size="sm" :disabled="!canAddItem" @click="addItem"><AppIcon name="add" /></b-button>
        </div>
        <div v-if="editModal.newItem.type === 'Cheque'" class="border rounded p-3 mb-3 bg-light">
          <b-row class="mb-2">
            <b-col cols="6"><b-form-select v-model="editModal.newItem.check.type" :options="[{value:'de_terceros',text:'Terceros'},{value:'propio',text:'Propio'}]" size="sm" /></b-col>
            <b-col cols="6"><b-form-input v-model="editModal.newItem.check.bank" placeholder="Banco" size="sm" /></b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="6"><b-form-input v-model="editModal.newItem.check.number" placeholder="Nro cheque" size="sm" /></b-col>
            <b-col cols="6"><b-form-input v-model="editModal.newItem.check.cuit" placeholder="CUIT" size="sm" /></b-col>
          </b-row>
          <b-row>
            <b-col cols="12"><b-form-input v-model="editModal.newItem.check.date_movement" type="date" size="sm" /></b-col>
          </b-row>
          <div v-if="!editModal.newItem.check.number || !editModal.newItem.check.cuit" class="text-danger small mt-2">Nro cheque y CUIT son obligatorios.</div>
        </div>
        <div v-if="editModal.items.length" class="mb-3 border rounded">
          <div v-for="(item, index) in editModal.items" :key="index" class="d-flex align-items-center px-3 py-2" :class="{ 'border-top': index > 0 }">
            <AppIcon :name="item.type === 'Efectivo' ? 'cash-stack' : 'credit-card'" :class="item.type === 'Efectivo' ? 'text-success' : 'text-primary'" class="mr-2" />
            <div class="flex-grow-1">
              <span class="small">{{ item.type }}</span>
              <small v-if="item.type === 'Cheque' && item.check.number" class="text-muted d-block">Nº {{ item.check.number }} · {{ item.check.bank || 'Sin banco' }}</small>
            </div>
            <strong class="mr-2">$ {{ formatMoney(item.amount) }}</strong>
            <b-button variant="link" class="p-0 text-danger" @click="editModal.items.splice(index, 1)"><AppIcon name="delete" /></b-button>
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <span class="text-muted small">Total cobranza:</span>
          <span class="font-weight-bold text-primary" style="font-size:1.05rem">$ {{ editModalTotal }}</span>
        </div>
        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-2" @click="editModal.active = false">Cancelar</b-button>
          <b-button variant="primary" :disabled="!editModal.items.length || editModal.saving" @click="saveEditPayment">
            <b-spinner small v-if="editModal.saving" class="mr-1" />Guardar
          </b-button>
        </div>
      </div>
    </b-modal>

    <div v-if="!loading && paymentRows.length" class="fixed-bottom mobile-surrender__footer p-3">
      <b-button variant="success" block size="lg" :disabled="submitting" @click="confirmSubmit">
        <b-spinner small v-if="submitting" class="mr-1" />
        <AppIcon v-else :name="isEditing ? 'send' : 'check2-circle'" class="mr-1" />
        {{ isEditing ? 'Volver a enviar' : 'Rendir ' + pendingPayments.length + ' cobranza' + (pendingPayments.length !== 1 ? 's' : '') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import serviceAPI from './services'
import PrinterServices from '@/components/inc/printer/services'
import Error from '@/handler/error'
import Helper from '@/handler/helper'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import moment from 'moment'
import { EventBus } from '@/handler/event-bus'

const emptyCheck = () => ({ type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' })

export default {
  data () {
    return {
      access: { module_id: Modules.INFORME_COBRANZAS, profile_id: Profiles.PERSONAL, view_reference: 'mobileSurrenderCreate', elements: {} },
      loading: false,
      submitting: false,
      skipInitialActivation: false,
      allPayments: [],
      surrendersToReview: [],
      surrender: null,
      form: { observations: '' },
      typeOptions: [{ value: 'Efectivo', text: 'Efectivo' }, { value: 'Cheque', text: 'Cheque' }],
      editModal: { active: false, key: 0, saving: false, payment: null, items: [], newItem: { type: 'Efectivo', amount: '', check: emptyCheck() } }
    }
  },
  computed: {
    surrenderId () { return this.$route.params.surrenderId },
    isEditing () { return !!this.surrenderId },
    pendingPayments () { return this.allPayments.filter(item => !item.surrender_detail_id) },
    paymentRows () {
      const payments = this.isEditing ? (this.surrender && this.surrender.detail ? this.surrender.detail.map(detail => detail.reports_payment).filter(Boolean) : []) : this.pendingPayments
      return payments.map(payment => {
        const items = payment.items || []
        const checks = items.filter(item => item.type === 'Cheque')
        return { id: payment.id, customer: payment.customer ? payment.customer.name : '—', date: payment.date, efectivo: this.sumItems(items, 'Efectivo'), cheque: this.sumItems(items, 'Cheque'), rawItems: items, chequeTooltip: this.checkTooltip(checks) }
      })
    },
    totalEfectivo () { return this.paymentRows.reduce((sum, item) => sum + item.efectivo, 0) },
    totalCheque () { return this.paymentRows.reduce((sum, item) => sum + item.cheque, 0) },
    totalGeneral () { return this.totalEfectivo + this.totalCheque },
    editModalTotal () { return this.formatMoney(this.editModal.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)) },
    canAddItem () { const item = this.editModal.newItem; return item.amount && parseFloat(item.amount) > 0 && (item.type !== 'Cheque' || (item.check.type && item.check.number && item.check.cuit)) }
  },
  watch: { '$route.params.surrenderId' () { this.load() } },
  mounted () { Helper.hasAccessView(this.access); this.load(); this.skipInitialActivation = true },
  activated () {
    if (this.skipInitialActivation) {
      this.skipInitialActivation = false
      return
    }
    this.load()
  },
  methods: {
    load () { this.isEditing ? this.loadSurrender() : this.loadPayments() },
    loadPayments () {
      const seller = Helper.getSeller(); if (!seller) return
      this.loading = true
      serviceAPI.obtenerReportsBySeller({ sellers_id: seller.id }).then(response => { this.allPayments = response.data; this.loading = false; this.loadSurrendersToReview() }).catch(error => { this.loading = false; this.$awn.alert(Error.showError(error)) })
    },
    loadSurrendersToReview () {
      const seller = Helper.getSeller(); if (!seller) return
      serviceAPI.filtrarReportSurrender({ sellers: [{ code: seller.id }], status: { code: 'Revisar' }, date_start: '2000-01-01', date_end: moment().format('YYYY-MM-DD') }).then(response => { this.surrendersToReview = response.data }).catch(() => {})
    },
    loadSurrender () {
      this.loading = true
      serviceAPI.mostrarSurrender(this.surrenderId).then(response => { this.surrender = response.data; this.form.observations = this.surrender.observations || ''; this.loading = false }).catch(error => { this.loading = false; this.$awn.alert(Error.showError(error)) })
    },
    openSurrender (surrender) { this.$router.push({ name: 'ReportsPaymentStaffMobileSurrenderCreate', params: { surrenderId: surrender.id } }) },
    sumItems (items, type) { return items.filter(item => item.type === type).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) },
    checkTooltip (checks) { return checks.map(check => { const parts = []; if (check.check_bank) parts.push('<b>Banco:</b> ' + check.check_bank); if (check.check_number) parts.push('<b>Nro:</b> ' + check.check_number); if (check.check_cuit) parts.push('<b>CUIT:</b> ' + check.check_cuit); if (check.check_type) parts.push(check.check_type === 'de_terceros' ? 'Terceros' : 'Propio'); parts.push('<b>$ ' + this.formatMoney(check.amount) + '</b>'); return parts.join(' · ') }).join('<br>') },
    openEditPayment (payment) { this.editModal.payment = payment; this.editModal.items = payment.rawItems.map(item => ({ type: item.type, amount: parseFloat(item.amount || 0), check: { type: item.check_type || 'de_terceros', bank: item.check_bank || '', number: item.check_number || '', cuit: item.check_cuit || '', date_movement: item.check_date_movement || '', observations: item.check_observations || '' } })); this.editModal.newItem = { type: 'Efectivo', amount: '', check: emptyCheck() }; this.editModal.key = Math.random(); this.editModal.active = true },
    addItem () { const item = this.editModal.newItem; if (!this.canAddItem) return; this.editModal.items.push({ type: item.type, amount: parseFloat(item.amount), check: { ...item.check } }); this.editModal.newItem = { type: item.type, amount: '', check: emptyCheck() } },
    saveEditPayment () { if (!this.editModal.items.length) return; this.editModal.saving = true; serviceAPI.actualizarItemsCobranza(this.editModal.payment.id, { detail: this.editModal.items.map(item => ({ type: item.type, amount: item.amount, check: item.check })) }).then(() => { this.editModal.saving = false; this.editModal.active = false; this.$awn.success('Cobranza actualizada'); this.load() }).catch(error => { this.editModal.saving = false; this.$awn.alert(Error.showError(error)) }) },
    confirmDialog (message, options) {
      const danger = !!options.danger
      const icon = options.icon || (danger ? 'warning' : 'help')
      const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: icon } }),
        options.title
      ])
      return this.$bvModal.msgBoxConfirm(message, {
        title: title,
        size: 'sm',
        buttonSize: 'sm',
        okVariant: danger ? 'danger' : 'dark',
        okTitle: options.okTitle,
        cancelTitle: options.cancelTitle || 'Cancelar',
        footerClass: 'p-2',
        headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
        hideHeaderClose: false,
        centered: true
      })
    },
    confirmSubmit () {
      const message = this.isEditing
        ? '¿Confirmás volver a enviar la rendición? Quedará como Pendiente de revisión del administrador.'
        : '¿Confirma rendir ' + this.pendingPayments.length + ' cobranza' + (this.pendingPayments.length !== 1 ? 's' : '') + ' por un total de $ ' + this.formatMoney(this.totalGeneral) + '?'
      this.confirmDialog(message, {
        title: this.isEditing ? 'Volver a enviar' : 'Confirmar rendición',
        icon: this.isEditing ? 'send' : 'check2-circle',
        okTitle: 'Confirmar'
      }).then(confirmed => { if (confirmed) this.submit() })
    },
    submit () {
      this.submitting = true
      const request = this.isEditing ? serviceAPI.editarSurrender({ id: this.surrender.id, sellers_id: this.surrender.sellers_id, date: this.surrender.date, shipping: this.surrender.shipping || '', observations: this.form.observations, review_observations: this.surrender.review_observations, status: 'Pendiente', read: false }) : serviceAPI.agregarSurrender({ sellers_id: Helper.getSeller().id, date: moment().format('YYYY-MM-DD'), observations: this.form.observations, payments: this.pendingPayments.map(item => item.id), read: false })
      request.then(response => { this.submitting = false; this.printSurrender(response.data.id); EventBus.$emit('ReportsPayment:SurrenderChanged'); this.$awn.success(this.isEditing ? 'Rendición enviada nuevamente' : 'Rendición enviada'); this.$router.push({ name: 'ReportsPaymentStaffCrudSurrenderSeller' }) }).catch(error => { this.submitting = false; this.$awn.alert(Error.showError(error)) })
    },
    printSurrender (id) { PrinterServices.generate({ reference: 'reports-payment-surrender', regID: id, staff_id: Helper.getStaffId() }).then(response => { window.open(response.data, 'Rendicion', 'toolbar=no,scrollbars=yes,location=no,statusbar=no,menubar=no,resizable=1,width=1000,height=800,left=200,top=80') }).catch(error => this.$awn.alert(Error.showError(error))) },
    formatMoney (value) { const number = parseFloat(value || 0); return isNaN(number) ? '0,00' : number.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    formatDate (date) { return moment(date).format('DD/MM/YYYY') }
  }
}
</script>

<style scoped>
.mobile-surrender { min-height: 100vh; padding-bottom: 80px; background: var(--app-color-canvas); }
.mobile-surrender__header { z-index: 500; background: var(--app-color-primary-900); }
.mobile-surrender__section-label { color: var(--app-color-text-muted); font-size: .75rem; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
.mobile-surrender__table-header { background: var(--app-color-surface-subtle); border-bottom: 1px solid var(--app-color-border); }
.mobile-surrender__payment-row { background: var(--app-color-surface); transition: background-color .15s; }
.mobile-surrender__payment-row:active { background: var(--app-color-surface-subtle); }
.mobile-surrender__amount { min-width: 110px; padding-left: 16px; }
.mobile-surrender__footer { z-index: 1030; background: var(--app-color-surface); border-top: 1px solid var(--app-color-border); }
</style>
